<template>
  <div>
    <PasswordReset
      v-if="showPasswordReset"
      @close="togglePasswordReset()"
    ></PasswordReset>
    <section>
      <v-container class="fill-height py-8" fluid>
        <v-row align="center" justify="center">
          <v-col cols="12" sm="8" md="4">
            <div class="v-responsive" style="max-width: 700px">
              <div class="v-responsive__content">
                <div class="text-overline secondary--text mb-3">Mira One</div>
                <h2 class="text-h3 mb-6">Anmelden</h2>
              </div>
            </div>
            <v-card max-width="600px" class="pa-4">
              <!-- Login form -->
              <v-card-text v-if="!showMFAStep">
                <v-form v-model="formIsValid" @submit.prevent>
                  <v-text-field
                    id="email"
                    v-model.trim="loginForm.email"
                    label="E-Mail-Adresse"
                    outlined
                    type="email"
                    autocomplete="email"
                    :rules="[rules.required, rules.emailFormat]"
                    placeholder="name@email.de"
                  ></v-text-field>
                  <v-text-field
                    id="password"
                    v-model.trim="loginForm.password"
                    label="Passwort"
                    outlined
                    type="password"
                    autocomplete="current-password"
                    :rules="[rules.required]"
                    placeholder="••••••••••••••••"
                  ></v-text-field>
                  <div class="pt-3 pb-6 body-2">
                    Mit deiner Anmeldung bestätigst Du, dass Du unsere
                    <a
                      target="_blank"
                      href="https://docs.mira-one.com/agb"
                      @click.stop
                      >Allgemeinen Geschäftsbedingungen</a
                    >
                    und
                    <a
                      target="_blank"
                      href="https://docs.mira-one.com/datenschutz"
                      @click.stop
                      >Datenschutzerklärung</a
                    >
                    gelesen und akzeptiert sowie in die Verarbeitung
                    personenbezogener Daten eingewilligt hast.
                  </div>
                </v-form>
                <v-btn
                  color="red darken-1"
                  :disabled="!formIsValid"
                  :loading="loading"
                  block
                  x-large
                  depressed
                  @click="login()"
                  class="white--text"
                >
                  Anmelden
                </v-btn>
                <div class="text-center my-3 text-subtitle-1">
                  <router-link
                    :to="{ name: 'auth-register' }"
                    class="text-decoration-none"
                    style="color: grey"
                    >Du hast noch kein Konto?</router-link
                  >
                </div>
                <div class="text-center">
                  <v-btn
                    class="uppercase"
                    plain
                    small
                    @click="togglePasswordReset()"
                    >Passwort vergessen</v-btn
                  >
                </div>

                <v-divider class="my-4"></v-divider>

                <div class="text-center">
                  <div class="text-caption text--secondary mb-3">
                    Andere Anmeldemethoden
                  </div>
                  <v-btn depressed block :to="{ name: 'auth-qr-login' }">
                    <v-icon left>mdi-qrcode-scan</v-icon>
                    Mit QR-Code anmelden
                  </v-btn>
                  <!-- <v-btn outlined block disabled class="mt-3">
                    <v-icon left>mdi-shield-link-variant</v-icon>
                    Mit Single-SignOn (SSO) anmelden
                  </v-btn> -->
                </div>
              </v-card-text>

              <!-- MFA step -->
              <v-card-text v-else>
                <div class="d-flex align-center mb-5">
                  <v-icon color="primary" large class="mr-3"
                    >mdi-shield-lock</v-icon
                  >
                  <div>
                    <div class="text-subtitle-1 font-weight-medium">
                      Zwei-Faktor-Authentifizierung
                    </div>
                    <div class="text-body-2 grey--text">
                      Gib den 6-stelligen Code aus Deiner Authenticator-App ein.
                    </div>
                  </div>
                </div>
                <v-text-field
                  v-model="mfaCode"
                  label="Bestätigungscode"
                  outlined
                  autofocus
                  maxlength="6"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  :rules="[rules.sixDigits]"
                  @keyup.enter="handleMFAVerification"
                ></v-text-field>
                <v-alert v-if="mfaErrorMsg" type="error" class="mb-4">
                  {{ mfaErrorMsg }}
                </v-alert>
                <v-btn
                  color="red darken-1"
                  :loading="loading"
                  :disabled="!mfaCode || mfaCode.length !== 6"
                  block
                  x-large
                  depressed
                  @click="handleMFAVerification"
                  class="white--text"
                >
                  Verifizieren
                </v-btn>
                <div class="text-center mt-3">
                  <v-btn plain small @click="cancelMFAVerification"
                    >Zurück zur Anmeldung</v-btn
                  >
                </div>
              </v-card-text>

              <v-card-text v-if="errorMsg !== '' && !showMFAStep">
                <v-alert dense text type="error"> {{ errorMsg }} </v-alert>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </div>
</template>

<script>
import { USER } from "@/store/modules.js";
import { GET_USER_PROFILE } from "@/store/action-types.js";
import PasswordReset from "@/components/Auth/PasswordReset";
import { handleAuthError } from "@/util/firebase/FirebaseErrorHandling.js";
import * as fb from "@/firebase";
import { TotpMultiFactorGenerator } from "firebase/auth";

export default {
  name: "auth-login",
  components: {
    PasswordReset,
  },
  data() {
    return {
      loginForm: {
        email: "",
        password: "",
      },
      showPasswordReset: false,
      showMFAStep: false,
      mfaResolver: null,
      mfaCode: "",
      mfaErrorMsg: "",
      loading: false,
      errorMsg: "",
      checkbox: false,
      formIsValid: false,

      rules: {
        emailFormat: (value) =>
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
            value,
          ) || "Ungültige E-Mail-Adresse",
        required: (value) => !!value || "Erforderlich",
      },
    };
  },

  methods: {
    togglePasswordReset() {
      this.showPasswordReset = !this.showPasswordReset;
    },

    async login() {
      if (this.formIsValid) {
        this.loading = true;
        this.errorMsg = "";

        try {
          // Sign in with email and password
          await fb.auth.signInWithEmailAndPassword(
            this.loginForm.email,
            this.loginForm.password,
          );

          // If successful, proceed with profile loading
          await this.$store.dispatch(`${USER}${GET_USER_PROFILE}`, {
            uid: fb.auth.currentUser.uid,
            allowRedirect: true,
          });
        } catch (error) {
          // Check if error is multi-factor auth required
          if (error.code === "auth/multi-factor-auth-required") {
            // MFA is required - show inline MFA step
            this.mfaResolver = error.resolver;
            this.showMFAStep = true;
            this.loading = false;
          } else {
            this.errorMsg = handleAuthError(error.code);
            console.log(error.code, error.message);
            this.loading = false;
          }
        }
      } else {
        alert("Fehler: Prüfe Deine Eingabe.");
      }
    },

    async handleMFAVerification() {
      this.loading = true;
      this.mfaErrorMsg = "";
      try {
        // Get the selected hint (should be TOTP)
        const selectedHint = this.mfaResolver.hints[0];

        // Create TOTP assertion
        const multiFactorAssertion =
          TotpMultiFactorGenerator.assertionForSignIn(
            selectedHint.uid,
            this.mfaCode,
          );

        // Resolve sign-in with the MFA assertion
        const userCredential = await this.mfaResolver.resolveSignIn(
          multiFactorAssertion,
        );

        // MFA verified, proceed with user profile loading
        this.showMFAStep = false;

        await this.$store.dispatch(`${USER}${GET_USER_PROFILE}`, {
          uid: userCredential.user.uid,
          allowRedirect: true,
        });
      } catch (error) {
        this.mfaErrorMsg = "Ungültiger Code. Bitte versuche es erneut.";
        console.error("MFA verification error:", error);
      } finally {
        this.loading = false;
      }
    },

    async cancelMFAVerification() {
      this.showMFAStep = false;
      this.mfaResolver = null;
      this.mfaCode = "";
      this.mfaErrorMsg = "";
    },
  },
};
</script>

<style>
.uppercase {
  text-transform: uppercase;
  letter-spacing: 0.0892857143em;
}
</style>
